import React, { EventHandler, useCallback, useMemo, useState } from 'react';
import {
  BackflowAssemblyInfo,
  InstallationStatus,
  JobFormDataInput,
} from '@ecosconnect/core/src/types/jobFormData';
import Accordion from '../../Accordion/Accordion';
import StatusChip from '../../Status/StatusChip';
import AccordionItem from '../../Accordion/AccordionItem';
import ButtonToggle from '../../Button/ButtonToggle';
import { yesNoOptions } from '../options';
import Button from '../../Button/Button';
import { Flex } from 'rebass';
import BackflowTestAssemblyValuesEdit from './BackflowTestAssemblyValuesEdit';
import { assemblyEdited } from '@ecosconnect/core/src/helpers/backflow/helpers';
import BackflowTestAssemblyValues from './BackflowTestAssemblyValues';
import { SetFormValue, SetFormValues } from '../../Job/JobThirdPane';
import { AssemblyInfo } from '@/components/Job/queries';

type BackflowTestAssemblyEditProps = {
  originalBackflowAssembly?: AssemblyInfo;
  backflowAssemblyId?: string | number;
  formAssembly?: AssemblyInfo;
  formData: JobFormDataInput;
  onFormInputChange: EventHandler<any>;
  setFormValue: SetFormValue;
  setFormValues: SetFormValues;
};

const BackflowTestAssemblyEdit: React.FC<BackflowTestAssemblyEditProps> = ({
  originalBackflowAssembly,
  backflowAssemblyId,
  formAssembly,
  formData,
  onFormInputChange,
  setFormValue,
  setFormValues,
}) => {
  const { backflowAssembly } = formData;

  const [editingAssembly, setEditingAssembly] = useState<boolean>(
    formData.installationStatus === InstallationStatus.REPLACEMENT ||
      assemblyEdited(
        formData.installationStatus,
        formData.originalBackflowAssembly,
        formData.backflowAssembly
      )
  );

  let flag: string | null = null;
  if (formData.improperlyInstalled) {
    flag = 'Improperly Installed';
  } else if (formData.unableToTest) {
    flag = 'Not Testable';
  } else if (formData.repairsRequired) {
    flag = 'Repairs Required';
  }

  const installationStatusBadge = useMemo(() => {
    if (flag) {
      return <StatusChip status="error">{flag}</StatusChip>;
    }

    switch (formData.installationStatus) {
      case InstallationStatus.NEW:
        return <StatusChip status="success">New</StatusChip>;
      case InstallationStatus.REPLACEMENT:
        return <StatusChip status="success">Replaced</StatusChip>;
      case InstallationStatus.REMOVED:
        return <StatusChip status="error">Removed</StatusChip>;
      default:
      case InstallationStatus.EXISTING:
        return <StatusChip status="default">Existing</StatusChip>;
    }
  }, [flag, formData.installationStatus]);

  const setAssemblyValues = useCallback(
    (updates: Partial<BackflowAssemblyInfo>) => {
      setFormValue('backflowAssembly', (oldAssembly) =>
        oldAssembly ? { ...oldAssembly, ...updates } : undefined
      );
    },
    [setFormValue]
  );

  const edit = () => {
    setEditingAssembly(true);
    if (
      !formData.backflowAssembly &&
      formData.installationStatus !== InstallationStatus.REMOVED
    ) {
      setFormValue('backflowAssembly', {} as any);
    }
  };

  const replace = useCallback(() => {
    if (!formData.originalBackflowAssembly) {
      return;
    }
    setEditingAssembly(true);
    setFormValues({
      installationStatus: InstallationStatus.REPLACEMENT,
      backflowAssembly: {
        type: formData.originalBackflowAssembly.type,
        size: formData.originalBackflowAssembly.size,
        location: formData.originalBackflowAssembly.location,
        waterSupplierId: formData.originalBackflowAssembly.waterSupplierId,
        serialNumber: '',
        installDate: formData.originalBackflowAssembly.installDate,
      },
    });
  }, [formData, setEditingAssembly, setFormValue]);

  const revert = useCallback(() => {
    setEditingAssembly(false);
    if (!formData.originalBackflowAssembly) {
      setFormValues({
        installationStatus: InstallationStatus.EXISTING,
        backflowAssembly: null,
      });
      return;
    }
    setFormValues({
      installationStatus: InstallationStatus.EXISTING,
      backflowAssembly: {
        ...formData.originalBackflowAssembly,
      },
    });
  }, [setEditingAssembly, setFormValue, formData.originalBackflowAssembly]);

  return (
    <Accordion title="Assembly" icon={installationStatusBadge}>
      <Flex
        flexDirection="row"
        sx={{ button: { mr: 2 } }}
        justifyContent="flex-start"
      >
        {!editingAssembly &&
          formData.installationStatus !== InstallationStatus.REMOVED && (
            <>
              <Button variant="secondary" icon="edit" onClick={edit}>
                Edit
              </Button>
              {formData.originalBackflowAssembly && (
                <Button variant="secondary" icon="repeat" onClick={replace}>
                  Replace
                </Button>
              )}
              <Button
                icon="trash"
                variant="secondary"
                name="installationStatus"
                value={InstallationStatus.REMOVED}
                onClick={onFormInputChange}
              >
                Remove
              </Button>
            </>
          )}
        {(editingAssembly ||
          formData.installationStatus === InstallationStatus.REMOVED) && (
          <>
            <Button variant="secondary" icon="undo" onClick={revert}>
              Revert
            </Button>
          </>
        )}
      </Flex>

      {backflowAssembly &&
        formData.installationStatus !== InstallationStatus.REMOVED && (
          <>
            {editingAssembly ? (
              <BackflowTestAssemblyValuesEdit
                setAssemblyValues={setAssemblyValues}
                formData={formData}
                setFormValue={setFormValue}
              />
            ) : (
              <BackflowTestAssemblyValues
                formData={formData}
                originalBackflowAssembly={originalBackflowAssembly}
                backflowAssemblyId={backflowAssemblyId}
                formAssembly={formAssembly}
              />
            )}

            <AccordionItem
              label="Unable to Test"
              alignItems="center"
              value={
                <ButtonToggle
                  name="unableToTest"
                  onClick={onFormInputChange}
                  buttonOptions={yesNoOptions}
                  my={-1}
                  value={
                    typeof formData.unableToTest !== 'number'
                      ? 0
                      : formData.unableToTest
                  }
                />
              }
            />
            <AccordionItem
              label="Improperly Installed"
              alignItems="center"
              value={
                <ButtonToggle
                  name="improperlyInstalled"
                  onClick={onFormInputChange}
                  buttonOptions={yesNoOptions}
                  my={-1}
                  value={
                    typeof formData.improperlyInstalled !== 'number'
                      ? 0
                      : formData.improperlyInstalled
                  }
                />
              }
            />
            <AccordionItem
              label="Repairs Required"
              alignItems="center"
              value={
                <ButtonToggle
                  name="repairsRequired"
                  onClick={onFormInputChange}
                  buttonOptions={yesNoOptions}
                  my={-1}
                  value={
                    typeof formData.repairsRequired !== 'number'
                      ? 0
                      : formData.repairsRequired
                  }
                />
              }
            />
          </>
        )}
    </Accordion>
  );
};

export default BackflowTestAssemblyEdit;
